/*
 * File: SalesOrderCard.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 16 Nov 2021 1:22:02 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Card, Col, Divider, Row, Tooltip, Typography } from 'antd';
import { SalesOrderStatus } from 'common/enums/SalesOrderStatus';
// import { BaseType } from 'antd/lib/typography/Base';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderModel } from 'models/SalesOrderModel';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { ReduxState } from 'redux/rootReducer';

/* ****************** Interfaces ****************** */
interface SalesOrderCardProps {
  salesOrder: SalesOrderModel;
}
const styles: InlineStylesModel = {
  card: {
    borderRadius: 5,
    height: 250
  },
  cardBody: {
    padding: 10,
    paddingRight: 15,
    paddingLeft: 15
  },
  largeText: {
    fontSize: '18px',
    fontFamily: 'Poppins',
    fontWeight: 700,
    color: '#000'
  },
  title: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#636770'
  },
  subTitle: {
    fontSize: '15px',
    color: '#A0A3AE',
    minHeight: '27px'
  },
  text: {
    fontSize: '13px',
    color: '#A0A3AE'
  },
  smallText: {
    fontSize: '11px',
    color: 'lightGrey'
  },
  status: {
    fontSize: '16px',
    fontWeight: 700
  },
  draft: {
    color: 'orange'
  },
  posted: {
    color: '#4E937A'
  },
  error: {
    color: 'red'
  },
  soNumber: {
    fontSize: '14px',
    color: '#5868DD'
  },
  poNumber: {
    fontSize: '14px',
    color: 'gray'
  },
  customerId: {
    fontSize: '16px',
    color: '#636770'
  }
};

export const SalesOrderCard = ({ salesOrder }: SalesOrderCardProps): JSX.Element => {
  const navigate = useNavigate();
  const { selectedCompanyDataAreaId } = useSelector((state: ReduxState) => state.salesOrders);

  /* ****************** Functions ****************** */
  const onSalesOrder = (): void => {
    if (salesOrder.status === SalesOrderStatus.DRAFT) {
      navigate(`drafts/${salesOrder.id}`);
    } else if (salesOrder.status === SalesOrderStatus.POSTED) {
      navigate(`orders/${salesOrder.id}`);
    } else {
      navigate('/create');
    }
  };

  const linkedCardJSX = (
    <Link to={salesOrder.status === SalesOrderStatus.DRAFT ? `drafts/${salesOrder.id}` : `posted/${salesOrder.id}`}>
      <div style={{ height: 100 }}>
        <Row gutter={8} justify="space-between">
          <Col span={15}>
            <Typography.Text ellipsis={{ tooltip: `${salesOrder.customerName}` }} style={styles.largeText}>
              <Tooltip title="Customer Name" placement="right" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
                {salesOrder.customerName}
              </Tooltip>
            </Typography.Text>
          </Col>
          <Col>
            <Typography style={salesOrder.status === 'Draft' ? { ...styles.status, ...styles.draft } : salesOrder.status === 'Posted' ? { ...styles.status, ...styles.posted } : salesOrder.status === 'PostedWithErrors' ? { ...styles.status, ...styles.error } : styles.status}>
              <Tooltip title="Status" placement="left" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
                {salesOrder.status === 'PostedWithErrors' ? 'Posted With Errors' : salesOrder.status}
              </Tooltip>
            </Typography>
          </Col>
        </Row>
        <Row gutter={8} justify="space-between">
          <Col>
            <Typography style={styles.customerId}>
              <Tooltip title="Customer Id" placement="right" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
                {salesOrder.customerId}
              </Tooltip>
            </Typography>
          </Col>
          <Col>
            {salesOrder.status === 'Draft' ? (
              <Typography style={styles.soNumber}>
                <Tooltip title="Draft Id" placement="left" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
                  {salesOrder.draftId}
                </Tooltip>
              </Typography>
            ) : (salesOrder.status === 'Posted' ||salesOrder.status === 'PostedWithErrors') ? (
              <Typography style={styles.soNumber}>
                <Tooltip title="Sales Order Number" placement="left" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
                  <Typography.Link
                    onClick={(e): void => e.stopPropagation()}
                    style={{ zIndex: 9999 }}
                    href={`${process.env.REACT_APP_LITTLE_MURRAY_SPARKLES_URL}/${selectedCompanyDataAreaId}/${salesOrder.customerId}/orderDetails/${salesOrder.salesOrderNumber}`}
                    target="_blank">
                    {salesOrder.salesOrderNumber}
                  </Typography.Link>
                </Tooltip>
              </Typography>
            ) : (
              <></>
            )}
          </Col>
        </Row>
        {salesOrder.quoteId && <Row gutter={8} justify="space-between">
          <Col>
            <Typography style={styles.subTitle}>
              <Tooltip title="Quote Id" placement="right" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
                {salesOrder.quoteId}
              </Tooltip>
            </Typography>
          </Col>
        </Row>}
        <Row gutter={8} justify="space-between">
          <Col>
            <Typography style={styles.subTitle}>
              <Tooltip title="Customer PO" placement="right" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
                {salesOrder.customerPo}
              </Tooltip>
            </Typography>
          </Col>
          <Col>
            <Typography style={styles.text}>
              <Tooltip title="Created By" placement="left" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
                {salesOrder.createdByUserFullName}
              </Tooltip>
            </Typography>
          </Col>
        </Row>
      </div>
      <Divider />
      <Row justify="space-between">
        <Col style={{ textAlign: 'start', marginLeft: 10 }} span={10}>
          <Typography.Paragraph style={{ fontSize: '14px', color: 'gray' }} ellipsis={{ tooltip: salesOrder.salesPersonFullName }}>
            <Tooltip title="Sales Person" placement="top" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
              {salesOrder.salesPersonFullName ? salesOrder.salesPersonFullName : 'N/A'}
            </Tooltip>
          </Typography.Paragraph>
        </Col>
        <Row>
          <Col span={2}>
            <Divider type="vertical" style={{ fontSize: 80 }} />
          </Col>
        </Row>

        <Col style={{ textAlign: 'end', marginRight: 10 }} span={10}>
          <Typography.Paragraph style={{ fontSize: '14px', color: 'gray' }}>
            <Tooltip title="Creation Date" placement="top" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
              {new Date(salesOrder.createdDateTime).toLocaleDateString()}
            </Tooltip>
          </Typography.Paragraph>
          <Typography.Paragraph style={{ fontSize: '14px', color: 'gray' }}>
            <Tooltip title="Shipping Date" placement="bottom" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
              {salesOrder.requestedShipDate ? new Date(salesOrder.requestedShipDate).toLocaleDateString() : 'N/A'}
            </Tooltip>
          </Typography.Paragraph>
        </Col>
      </Row>
    </Link>
  );

  /* ****************** Render ****************** */
  return (
    <Card style={styles.card} onClick={salesOrder.status === SalesOrderStatus.DRAFT ? onSalesOrder : undefined} bodyStyle={styles.cardBody} hoverable>
      {linkedCardJSX}
    </Card>
  );
};
